<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import {
  Sparkles,
  Heart,
  Briefcase,
  Lightbulb,
  RotateCcw,
  Table2,
  Eye,
  AlertTriangle,
  Layers,
  Link2,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
} from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MAJOR_ARCANA_SLUGS,
  MINOR_ARCANA_SLUGS,
  SUIT_LABELS,
  cardPaper,
  arcanaLabel,
  cardImagePath,
  cardImageSrc,
} from '@app/data/cardSeoData';
import {
  recommendedSpread,
  cardAppCta,
  cardTelegramDeepLink,
  askTheCatQuestions,
} from '@app/data/kartyLinks';
import {
  TELEGRAM_CTA_LABEL,
} from '@app/presentation/components/site/constants';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import PaperLetter from '@app/presentation/components/paper/PaperLetter.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoCardSticker from '@app/presentation/components/seo/SeoCardSticker.vue';
import SeoFaqItem from '@app/presentation/components/seo/SeoFaqItem.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const route = useRoute();
const router = useRouter();

const slug = route.params.slug as string;
const card = CARD_SEO_DATA[slug];

if (!card) {
  router.replace('/karty');
}

// Real card face (same-origin /cards asset → crawlable on the pre-rendered page).
// avif/webp/jpg variants for the hero <picture>; this is the LCP image of the page.
const cardImages = card ? cardImageSrc(card.slug) : { avif: '', webp: '', jpg: '' };

// Canonical deck order (Major 0–XXI, then minors) — used for prev/next nav and
// for the zero-padded absolute card-image URL in JSON-LD.
const DECK_ORDER: string[] = [...MAJOR_ARCANA_SLUGS, ...MINOR_ARCANA_SLUGS];

// Contextual conversion CTAs — carry the card context into the app/bot.
const appCta = card ? cardAppCta(card.slug) : '';
const telegramDeepLink = card ? cardTelegramDeepLink(card.slug) : '';

// Recommended spread to bridge from the "как читать в раскладе" block.
const spread = card ? recommendedSpread(card) : null;

// Preset "ask the cat" questions, deep-linked into the bot.
const catQuestions = card ? askTheCatQuestions(card) : [];

// Card combinations resolved to the partner card's display name (skip unknowns).
const cardCombinations = computed(() =>
  card
    ? card.combinations
        .map((combo) => {
          const partner = CARD_SEO_DATA[combo.slug];
          return partner ? { slug: combo.slug, name: partner.name, hint: combo.hint } : null;
        })
        .filter((c): c is { slug: string; name: string; hint: string } => Boolean(c))
    : [],
);

// Соответствия table rows (skip '—' placeholders so empty rows don't render).
const correspondenceRows = computed(() => {
  if (!card) return [];
  const c = card.correspondences;
  return [
    { label: 'Стихия', value: c.element },
    { label: 'Астрология', value: c.astrology },
    { label: 'Число', value: c.numerology },
    { label: 'Тайминг', value: c.timing },
  ].filter((row) => row.value && row.value !== '—');
});

const yesNoBadge = computed(() => {
  if (!card) return null;
  const m = {
    yes: { text: 'Да', cls: 'bg-success text-success-foreground' },
    no: { text: 'Нет', cls: 'bg-destructive text-destructive-foreground' },
    maybe: { text: 'Зависит', cls: 'bg-warning text-warning-foreground' },
  } as const;
  return m[card.correspondences.yesNo];
});

// Перевёрнутые оттенки → three labelled mini-cards.
const reversedShadeCards = computed(() =>
  card
    ? [
        { label: 'Энергия заблокирована', text: card.reversedShades.blocked },
        { label: 'В избытке', text: card.reversedShades.excess },
        { label: 'На выходе', text: card.reversedShades.ending },
      ]
    : [],
);

// The card's paper (its arcana / suit stock + fabric) for the cat's-wisdom patch.
const paper = card ? cardPaper(card) : { stock: '', fabric: '' };

// Seeded tilts for the question slips (index-based — identical on SSR + client).
const SLIP_TILTS = [-0.5, 0.4, -0.3, 0.6];

// Prev/next card in canonical deck order (wraps around the 78-card deck).
const deckNav = computed(() => {
  if (!card) return { prev: null, next: null };
  const i = DECK_ORDER.indexOf(card.slug);
  if (i < 0) return { prev: null, next: null };
  const prevSlug = DECK_ORDER[(i - 1 + DECK_ORDER.length) % DECK_ORDER.length];
  const nextSlug = DECK_ORDER[(i + 1) % DECK_ORDER.length];
  return { prev: CARD_SEO_DATA[prevSlug] ?? null, next: CARD_SEO_DATA[nextSlug] ?? null };
});

// Absolute https URL of the card face for JSON-LD (the page itself uses base-relative paths).
const cardImageAbsolute = computed(() => {
  if (!card) return 'https://tarotmeow.ru/og-image.jpg';
  const i = DECK_ORDER.indexOf(card.slug);
  return i < 0
    ? 'https://tarotmeow.ru/og-image.jpg'
    : `https://tarotmeow.ru/cards/${String(i).padStart(2, '0')}.jpg`;
});

// Chip under the breadcrumb: arcana + roman number (major) or suit + rank (minor).
const chipLabel = computed(() => {
  if (!card) return '';
  if (card.arcana === 'major') return `${arcanaLabel(card)} · ${card.number}`;
  const suit = card.suit ? SUIT_LABELS[card.suit] : '';
  return `${suit} · ${card.number}`;
});

const uprightAspects = computed(() =>
  card
    ? [
        { icon: Heart, label: 'В любви', text: card.upright.love },
        { icon: Briefcase, label: 'В работе и финансах', text: card.upright.career },
        { icon: Lightbulb, label: 'Совет', text: card.upright.advice },
      ]
    : [],
);

const reversedAspects = computed(() =>
  card
    ? [
        { icon: Heart, label: 'В любви', text: card.reversed.love },
        { icon: Briefcase, label: 'В работе и финансах', text: card.reversed.career },
        { icon: Lightbulb, label: 'Совет', text: card.reversed.advice },
      ]
    : [],
);

// Related cards: same arcana (majors) or same suit (minors), neighbours first for
// variety. Computed at setup scope so vite-ssg bakes the <a href> links into HTML.
const relatedCards = computed(() => {
  if (!card) return [];
  const pool =
    card.arcana === 'major'
      ? MAJOR_ARCANA_SLUGS
      : MINOR_ARCANA_SLUGS.filter((s) => CARD_SEO_DATA[s]?.suit === card.suit);
  const i = pool.indexOf(slug);
  const ordered = i >= 0 ? [...pool.slice(i + 1), ...pool.slice(0, i)] : pool;
  return ordered
    .map((s) => CARD_SEO_DATA[s])
    .filter(Boolean)
    .slice(0, 4);
});

if (card) {
  useHead({
    title: card.metaTitle,
    meta: [
      { name: 'description', content: card.metaDescription },
      { name: 'keywords', content: card.keywords.join(', ') },
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: card.metaTitle },
      { property: 'og:description', content: card.metaDescription },
      { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
      { property: 'og:locale', content: 'ru_RU' },
      { property: 'og:site_name', content: 'Таро Мяу' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: card.metaTitle },
      { name: 'twitter:description', content: card.metaDescription },
      { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    ],
    link: [
      { rel: 'canonical', href: `https://tarotmeow.ru/karty/${card.slug}` },
      { rel: 'alternate', hreflang: 'ru', href: `https://tarotmeow.ru/karty/${card.slug}` },
      { rel: 'alternate', hreflang: 'x-default', href: `https://tarotmeow.ru/karty/${card.slug}` },
    ],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
            { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
            { '@type': 'ListItem', position: 3, name: card.name, item: `https://tarotmeow.ru/karty/${card.slug}` },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'DefinedTerm',
          name: card.name,
          identifier: card.slug,
          description: card.tldr,
          inLanguage: 'ru',
          url: `https://tarotmeow.ru/karty/${card.slug}`,
          inDefinedTermSet: {
            '@type': 'DefinedTermSet',
            name: 'Таро — значения 78 карт',
            url: 'https://tarotmeow.ru/karty',
          },
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: card.metaTitle,
          description: card.tldr,
          inLanguage: 'ru',
          author: { '@type': 'Organization', name: 'Таро Мяу' },
          publisher: {
            '@type': 'Organization',
            name: 'Таро Мяу',
            alternateName: 'Tarot Meow',
            logo: { '@type': 'ImageObject', url: 'https://tarotmeow.ru/og-image.jpg' },
          },
          datePublished: '2026-06-18',
          dateModified: '2026-06-18',
          mainEntityOfPage: `https://tarotmeow.ru/karty/${card.slug}`,
          // Primary image = the card face (linked by @id to the ImageObject below),
          // not the generic OG banner — the card face is what defines this page.
          image: { '@id': `https://tarotmeow.ru/karty/${card.slug}#cardimage` },
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          '@id': `https://tarotmeow.ru/karty/${card.slug}#cardimage`,
          contentUrl: cardImageAbsolute.value,
          url: cardImageAbsolute.value,
          caption: `Карта Таро ${card.name}, колода Moon Magic Tarot`,
          creditText: 'Moon Magic Tarot',
          width: 275,
          height: 483,
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: card.faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }),
      },
    ],
  });
}
</script>

<template>
  <article
    v-if="card"
    class="pb-4"
  >
    <!-- ============================ HERO ============================ -->
    <!-- The card as a big sticker (the LCP image — eager, high priority, visible in the
         static HTML) beside its name; on phones the sticker comes first. Not boxed. -->
    <section class="pt-6 pb-14 md:pt-10 md:pb-20">
      <div class="site-container">
        <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Карты', to: '/karty' }, { label: card.name }]" />

        <div class="flex flex-col items-center gap-9 text-center md:flex-row md:items-center md:gap-14 md:text-left">
          <div class="flex-1 space-y-5">
            <div class="flex flex-wrap items-center justify-center gap-2.5 md:justify-start">
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: -1.5deg"
              >
                {{ chipLabel }}
              </span>
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: 1deg"
              >
                <Sparkles
                  class="h-3 w-3"
                  aria-hidden="true"
                /> Разбор карты
              </span>
            </div>

            <h1 class="type-display break-words">{{ card.name }}</h1>

            <p class="type-body mx-auto max-w-[560px] text-muted-foreground md:mx-0">
              Значение карты {{ card.name }} в прямом и перевёрнутом положении: любовь, работа, совет и ответы на частые вопросы.
            </p>

            <div class="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap sm:justify-center md:justify-start">
              <Button
                as-child
                size="lg"
              >
                <RouterLink :to="appCta">Расклад с картой {{ card.name }}</RouterLink>
              </Button>
              <Button
                as-child
                variant="outline"
                size="lg"
              >
                <a
                  :href="telegramDeepLink"
                  target="_blank"
                  rel="noopener"
                >{{ TELEGRAM_CTA_LABEL }}</a>
              </Button>
            </div>
          </div>

          <span
            v-if="cardImages.jpg"
            class="seo-sticker order-first w-40 shrink-0 md:order-none md:mr-4 md:w-56"
            style="--tilt: -2deg"
          >
            <picture class="contents">
              <source
                type="image/avif"
                :srcset="cardImages.avif"
              >
              <source
                type="image/webp"
                :srcset="cardImages.webp"
              >
              <img
                :src="cardImages.jpg"
                :alt="`Карта Таро ${card.name}`"
                width="275"
                height="483"
                fetchpriority="high"
                decoding="async"
                class="aspect-[275/483] w-full object-cover"
              >
            </picture>
          </span>
        </div>
      </div>
    </section>

    <!-- ============================ TL;DR ============================ -->
    <!-- The gist on a taped note. -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mx-auto max-w-[820px]">
          <PaperLetter>
            <div class="type-overline mb-3 flex items-center gap-2 text-primary-ink">
              <Sparkles
                class="h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>Если коротко</span>
            </div>
            <p class="letter-text type-body whitespace-pre-line md:text-lg">{{ card.tldr }}</p>
          </PaperLetter>
        </ScrollReveal>
      </div>
    </section>

    <!-- ===================== ПРЯМОЕ ПОЛОЖЕНИЕ ===================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Прямое положение</div>
          <h2 class="type-title">Значение карты {{ card.name }}</h2>
        </ScrollReveal>

        <ScrollReveal class="mb-8 max-w-[760px]">
          <p class="type-body whitespace-pre-line text-muted-foreground md:text-lg">{{ card.upright.meaning }}</p>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ScrollReveal
            v-for="(aspect, i) in uprightAspects"
            :key="aspect.label"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col gap-3 p-5 md:p-6">
              <div class="flex items-center gap-2.5">
                <span
                  class="seo-disc h-8 w-8"
                  aria-hidden="true"
                >
                  <component
                    :is="aspect.icon"
                    class="h-4 w-4"
                    :stroke-width="2.25"
                  />
                </span>
                <h3 class="type-app-heading text-foreground">{{ aspect.label }}</h3>
              </div>
              <p class="type-body text-muted-foreground">{{ aspect.text }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ========================= СООТВЕТСТВИЯ ========================= -->
    <section
      v-if="correspondenceRows.length || yesNoBadge"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <Table2
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> Соответствия
          </div>
          <h2 class="type-title">Карта {{ card.name }} в деталях</h2>
        </ScrollReveal>

        <ScrollReveal
          :delay="80"
          class="max-w-[760px]"
        >
          <div class="paper-sheet px-5 md:px-6">
            <table class="w-full border-collapse text-left">
              <tbody class="seo-lines">
                <tr
                  v-for="row in correspondenceRows"
                  :key="row.label"
                >
                  <th
                    scope="row"
                    class="type-label w-[40%] py-4 pr-4 align-top text-muted-foreground"
                  >
                    {{ row.label }}
                  </th>
                  <td class="type-body py-4 align-top text-foreground">{{ row.value }}</td>
                </tr>
                <tr v-if="yesNoBadge">
                  <th
                    scope="row"
                    class="type-label w-[40%] py-4 pr-4 align-top text-muted-foreground"
                  >
                    Ответ «да/нет»
                  </th>
                  <td class="py-4 align-top text-foreground">
                    <span
                      class="seo-tag type-overline px-2.5 py-0.5"
                      :class="yesNoBadge.cls"
                      style="--tilt: -2deg"
                    >{{ yesNoBadge.text }}</span>
                    <p
                      v-if="card.correspondences.yesNoReason"
                      class="type-caption mt-2.5"
                    >
                      {{ card.correspondences.yesNoReason }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- ========================= СИМВОЛИКА ========================= -->
    <section
      v-if="card.symbols.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <Eye
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> Что скрыто на карте
          </div>
          <h2 class="type-title">Символика карты</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ScrollReveal
            v-for="(item, i) in card.symbols"
            :key="item.symbol"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col gap-2 p-5 md:p-6">
              <h3 class="type-app-heading text-foreground">{{ item.symbol }}</h3>
              <p class="type-body text-muted-foreground">{{ item.meaning }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ================== ПЕРЕВЁРНУТОЕ ПОЛОЖЕНИЕ ================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <RotateCcw
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> Перевёрнутое положение
          </div>
          <h2 class="type-title">{{ card.name }} в перевёрнутом положении</h2>
        </ScrollReveal>

        <ScrollReveal class="mb-8 max-w-[760px]">
          <p class="type-body whitespace-pre-line text-muted-foreground md:text-lg">{{ card.reversed.meaning }}</p>
        </ScrollReveal>

        <div class="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ScrollReveal
            v-for="(aspect, i) in reversedAspects"
            :key="aspect.label"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col gap-3 p-5 md:p-6">
              <div class="flex items-center gap-2.5">
                <span
                  class="seo-disc h-8 w-8"
                  aria-hidden="true"
                >
                  <component
                    :is="aspect.icon"
                    class="h-4 w-4"
                    :stroke-width="2.25"
                  />
                </span>
                <h3 class="type-app-heading text-foreground">{{ aspect.label }}</h3>
              </div>
              <p class="type-body text-muted-foreground">{{ aspect.text }}</p>
            </div>
          </ScrollReveal>
        </div>

        <!-- Оттенки перевёрнутого значения -->
        <ScrollReveal class="mb-5">
          <h3 class="type-subheading text-foreground">Оттенки перевёрнутого значения</h3>
        </ScrollReveal>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ScrollReveal
            v-for="(shade, i) in reversedShadeCards"
            :key="shade.label"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col gap-2 p-5 md:p-6">
              <h4 class="type-app-heading text-primary-ink">{{ shade.label }}</h4>
              <p class="type-body text-muted-foreground">{{ shade.text }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ===================== ОШИБКИ ТОЛКОВАНИЯ ==================== -->
    <!-- A sheet per myth: the myth crossed out, the truth ticked (the Codex myth-buster). -->
    <section
      v-if="card.misreadings.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <AlertTriangle
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> Без мифов
          </div>
          <h2 class="type-title">Частые ошибки толкования</h2>
        </ScrollReveal>

        <div class="max-w-[820px] space-y-4">
          <ScrollReveal
            v-for="(item, i) in card.misreadings"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet overflow-hidden">
              <div class="cd-myth flex gap-3 px-5 py-4 md:px-6">
                <span
                  class="cd-mark cd-mark-wrong mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  aria-hidden="true"
                >
                  <X
                    class="h-3.5 w-3.5"
                    :stroke-width="3"
                  />
                </span>
                <div>
                  <p class="type-overline mb-1 text-muted-foreground">Миф</p>
                  <p class="type-body text-muted-foreground line-through decoration-muted-foreground/50">{{ item.myth }}</p>
                </div>
              </div>
              <div class="cd-truth flex gap-3 px-5 py-4 md:px-6">
                <span
                  class="cd-mark cd-mark-right mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  aria-hidden="true"
                >
                  <Check
                    class="h-3.5 w-3.5"
                    :stroke-width="3"
                  />
                </span>
                <div>
                  <p class="type-overline mb-1 text-foreground">На самом деле</p>
                  <p class="type-body text-foreground">{{ item.truth }}</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ==================== КАК ЧИТАТЬ В РАСКЛАДЕ ================== -->
    <section
      v-if="card.positions.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <Layers
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> На позициях расклада
          </div>
          <h2 class="type-title">Как читать в раскладе</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ScrollReveal
            v-for="(pos, i) in card.positions"
            :key="pos.position"
            :delay="((i % 2) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col gap-2 p-5 md:p-6">
              <h3 class="type-app-heading text-primary-ink">{{ pos.position }}</h3>
              <p class="type-body text-muted-foreground">{{ pos.reading }}</p>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal
          v-if="spread"
          :delay="160"
          class="mt-8 text-center"
        >
          <Button
            as-child
            size="lg"
          >
            <RouterLink :to="`/rasklady/${spread.slug}`">Сделать {{ spread.title }} →</RouterLink>
          </Button>
        </ScrollReveal>
      </div>
    </section>

    <!-- ======================= СОЧЕТАНИЯ КАРТ ===================== -->
    <section
      v-if="cardCombinations.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <Link2
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> В паре с другими картами
          </div>
          <h2 class="type-title">Сочетания карт</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ScrollReveal
            v-for="(combo, i) in cardCombinations"
            :key="combo.slug"
            :delay="((i % 3) + 1) * 80"
          >
            <RouterLink
              :to="`/karty/${combo.slug}`"
              class="cd-lift seo-press paper-sheet group flex h-full flex-col gap-2 p-5 md:p-6"
            >
              <span class="type-app-heading text-foreground">{{ card.name }} + {{ combo.name }}</span>
              <p class="type-body text-muted-foreground">{{ combo.hint }}</p>
              <span class="type-label mt-auto pt-1 text-primary-ink group-hover:underline group-hover:underline-offset-4">Открыть карту {{ combo.name }} →</span>
            </RouterLink>
          </ScrollReveal>
        </div>

        <ScrollReveal class="mt-8 text-center">
          <RouterLink
            to="/karty/sochetaniya"
            class="seo-more type-label inline-flex items-center gap-1"
          >
            Все сочетания карт Таро →
          </RouterLink>
        </ScrollReveal>
      </div>
    </section>

    <!-- =========================== FAQ ============================ -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Вопросы и ответы</div>
          <h2 class="type-title">Частые вопросы о карте</h2>
        </ScrollReveal>

        <div class="max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(faq, i) in card.faq"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <SeoFaqItem
              :question="faq.question"
              :answer="faq.answer"
            />
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ======================= ПОХОЖИЕ КАРТЫ ====================== -->
    <section
      v-if="relatedCards.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8 flex items-end justify-between gap-4">
          <div>
            <div class="type-overline mb-3.5 text-primary-ink">Продолжай изучать</div>
            <h2 class="type-title">Похожие карты</h2>
          </div>
          <RouterLink
            to="/karty"
            class="seo-more type-label hidden shrink-0 sm:block"
          >
            Все карты →
          </RouterLink>
        </ScrollReveal>

        <ul class="grid max-w-[760px] grid-cols-4 gap-x-4 gap-y-5 sm:gap-x-6">
          <li
            v-for="(related, i) in relatedCards"
            :key="related.slug"
          >
            <SeoCardSticker
              :slug="related.slug"
              :name="related.name"
              :number="related.number"
              :index="i"
            />
          </li>
        </ul>
      </div>
    </section>

    <!-- ====================== СПРОСИ КОТА ========================= -->
    <!-- Ready-made questions on cream slips, like the app's question step. -->
    <section
      v-if="catQuestions.length"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 flex items-center gap-2 text-primary-ink">
            <MessageCircle
              class="h-3.5 w-3.5"
              aria-hidden="true"
            /> Живой разбор
          </div>
          <h2 class="type-title">Спроси кота про эту карту</h2>
        </ScrollReveal>

        <div class="flex max-w-[820px] flex-col gap-3.5">
          <ScrollReveal
            v-for="(q, i) in catQuestions"
            :key="q.label"
            :delay="((i % 3) + 1) * 80"
          >
            <a
              :href="q.href"
              target="_blank"
              rel="noopener"
              class="seo-slip seo-press paper-tag group flex items-center justify-between gap-4 px-5 py-4"
              :style="{ '--tilt': `${SLIP_TILTS[i % SLIP_TILTS.length]}deg` }"
            >
              <span class="type-body text-left text-paper-stock-ink">{{ q.label }}</span>
              <span class="type-label shrink-0 text-paper-stock-ink group-hover:underline group-hover:underline-offset-4">Спросить →</span>
            </a>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ===================== КОШАЧЬЯ МУДРОСТЬ ===================== -->
    <!-- The cat's line on a patch of the card's own fabric (its arcana / suit), the cat
         looking in from its corner (cut edges past the patch's edges) — he's the one talking. -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mx-auto max-w-[760px]">
          <div class="relative">
            <blockquote
              class="paper-sheet relative overflow-hidden px-7 pb-32 pt-9 md:px-12 md:py-12 md:pr-44"
              :class="[paper.stock, paper.fabric]"
            >
              <div
                class="pointer-events-none absolute -bottom-2 -right-2 z-[1] w-[128px] md:w-[160px]"
                aria-hidden="true"
              >
                <PaperCutout
                  name="peek_cat"
                  still
                  lazy
                />
              </div>
              <div class="relative z-[1]">
                <div class="type-overline mb-3.5 flex items-center gap-2 text-foreground/80">
                  <Sparkles
                    class="h-3.5 w-3.5"
                    aria-hidden="true"
                  /> Кот говорит
                </div>
                <p class="type-subheading whitespace-pre-line text-foreground">«{{ card.catWisdom }}»</p>
              </div>
            </blockquote>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- =============== ПРЕДЫДУЩАЯ / СЛЕДУЮЩАЯ КАРТА ============== -->
    <!-- Two paper tabs with the neighbours as tiny stickers. -->
    <section
      v-if="deckNav.prev || deckNav.next"
      class="pb-14 md:pb-20"
    >
      <div class="site-container">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ScrollReveal v-if="deckNav.prev">
            <RouterLink
              :to="`/karty/${deckNav.prev.slug}`"
              class="cd-lift seo-press paper-sheet flex h-full items-center gap-4 p-4 md:p-5"
            >
              <ChevronLeft
                class="h-5 w-5 shrink-0 text-primary-ink"
                aria-hidden="true"
              />
              <span class="cd-mini">
                <picture class="contents">
                  <source
                    type="image/avif"
                    :srcset="cardImageSrc(deckNav.prev.slug).avif"
                  >
                  <source
                    type="image/webp"
                    :srcset="cardImageSrc(deckNav.prev.slug).webp"
                  >
                  <img
                    :src="cardImagePath(deckNav.prev.slug)"
                    :alt="`Карта Таро ${deckNav.prev.name}`"
                    width="275"
                    height="483"
                    loading="lazy"
                    decoding="async"
                    class="h-16 w-[36px] object-cover"
                  >
                </picture>
              </span>
              <div class="min-w-0">
                <span class="type-caption block">Предыдущая карта</span>
                <span class="type-app-heading block truncate text-foreground">{{ deckNav.prev.name }}</span>
              </div>
            </RouterLink>
          </ScrollReveal>

          <ScrollReveal
            v-if="deckNav.next"
            :delay="80"
            :class="!deckNav.prev ? 'sm:col-start-2' : ''"
          >
            <RouterLink
              :to="`/karty/${deckNav.next.slug}`"
              class="cd-lift seo-press paper-sheet flex h-full items-center justify-end gap-4 p-4 text-right md:p-5"
            >
              <div class="min-w-0">
                <span class="type-caption block">Следующая карта</span>
                <span class="type-app-heading block truncate text-foreground">{{ deckNav.next.name }}</span>
              </div>
              <span class="cd-mini">
                <picture class="contents">
                  <source
                    type="image/avif"
                    :srcset="cardImageSrc(deckNav.next.slug).avif"
                  >
                  <source
                    type="image/webp"
                    :srcset="cardImageSrc(deckNav.next.slug).webp"
                  >
                  <img
                    :src="cardImagePath(deckNav.next.slug)"
                    :alt="`Карта Таро ${deckNav.next.name}`"
                    width="275"
                    height="483"
                    loading="lazy"
                    decoding="async"
                    class="h-16 w-[36px] object-cover"
                  >
                </picture>
              </span>
              <ChevronRight
                class="h-5 w-5 shrink-0 text-primary-ink"
                aria-hidden="true"
              />
            </RouterLink>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ========================= BOTTOM CTA ======================== -->
    <div class="site-container py-6 md:py-10">
      <ScrollReveal>
        <SeoFinalCta>
          <h2 class="type-display">
            Вытяни карту <span class="text-primary-ink">{{ card.name }}</span> в раскладе
          </h2>
          <p class="type-body mx-auto mb-8 mt-[18px] max-w-[520px] text-muted-foreground md:text-lg">
            Первый расклад бесплатно — узнай, что эта карта значит именно для тебя, с живой интерпретацией.
          </p>
          <div class="flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="appCta">Вытянуть карту {{ card.name }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="telegramDeepLink"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/karty">Все карты</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </article>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
/* Myth / truth rows: a light intent wash over the sheet; the cross / tick carries the meaning. */
.cd-myth {
  background: color-mix(in oklch, var(--destructive) 13%, transparent);
}
.cd-truth {
  background: color-mix(in oklch, var(--success) 13%, transparent);
}
.cd-mark-wrong {
  background: var(--destructive);
  color: var(--destructive-foreground);
}
.cd-mark-right {
  background: var(--success);
  color: var(--success-foreground);
}
/* Paper pieces that are links lift a hair on hover. */
@media (hover: hover) {
  .cd-lift:hover {
    translate: 0 -2px;
  }
}
/* A tiny sticker: the neighbour card with a cream die-cut ring. */
.cd-mini {
  display: block;
  flex-shrink: 0;
  rotate: -3deg;
  border-radius: 4px;
  background: var(--paper-stock);
  box-shadow: 0 0 0 2px var(--paper-stock), 0 4px 8px -3px var(--paper-shade);
}
.cd-mini img {
  display: block;
  border-radius: 3px;
  background: var(--muted);
}
</style>
